import React from 'react'
import { Drawer, Box, Typography, IconButton, Divider, CircularProgress, Chip, Grid, Avatar } from '@mui/material'
import { IconSeeker, CardSeeker } from '@ui-seeker/core'
import {
    userDetailOpen,
    selectedUser,
    userStatsData,
    userActivityData,
    isLoadingUserDetail
} from './@signals'
import DailyChart from './DailyChart'

export default function UserDetailDrawer() {
    const handleClose = () => {
        userDetailOpen.value = false
        selectedUser.value = null
    }

    const user = selectedUser.value
    const stats = userStatsData.value
    const activity = userActivityData.value

    // Use data from selected user row if stats not loaded yet
    const displayData = stats || user

    return (
        <Drawer
            anchor="right"
            open={userDetailOpen.value}
            onClose={handleClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 480 } }
            }}
        >
            <Box sx={{ p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">User Details</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <IconSeeker icon="xmark" />
                    </IconButton>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {isLoadingUserDetail.value ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : displayData ? (
                    <Box>
                        {/* User info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Avatar
                                sx={{
                                    width: 56,
                                    height: 56,
                                    bgcolor: user?.is_system ? 'grey.400' : 'primary.main',
                                    fontSize: '1.5rem'
                                }}
                            >
                                {user?.is_system ? (
                                    <IconSeeker icon="robot" size="lg" />
                                ) : (
                                    user?.user_email?.charAt(0)?.toUpperCase() || '?'
                                )}
                            </Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    {user?.user_email || 'Unknown'}
                                </Typography>
                                <Chip
                                    label={user?.is_system ? 'System (Automated)' : 'Human'}
                                    size="small"
                                    color={user?.is_system ? 'default' : 'primary'}
                                    variant="outlined"
                                />
                            </Box>
                        </Box>

                        {/* Stats cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6}>
                                <CardSeeker sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h4" color="primary.main">
                                        {(displayData.total_mappings || displayData.total_actions || 0).toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Actions
                                    </Typography>
                                </CardSeeker>
                            </Grid>
                            <Grid item xs={6}>
                                <CardSeeker sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h4" color="success.main">
                                        {displayData.rate_per_hour?.toFixed(1) || displayData.daily_average || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Rate / Hour
                                    </Typography>
                                </CardSeeker>
                            </Grid>
                        </Grid>

                        {/* By Action */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>By Action Type</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {displayData.by_action ? (
                                    Object.entries(displayData.by_action).map(([action, count]) => (
                                        <Chip
                                            key={action}
                                            label={`${action}: ${count.toLocaleString()}`}
                                            size="small"
                                            color={action === 'MAP' ? 'success' : action === 'UNMAP' ? 'error' : 'warning'}
                                        />
                                    ))
                                ) : (
                                    <>
                                        <Chip label={`MAP: ${(displayData.map_count || 0).toLocaleString()}`} size="small" color="success" />
                                        <Chip label={`UNMAP: ${(displayData.unmap_count || 0).toLocaleString()}`} size="small" color="error" />
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* By Source */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>By Source</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {displayData.by_source && Object.keys(displayData.by_source).length > 0 ? (
                                    Object.entries(displayData.by_source).map(([source, count]) => (
                                        <Chip
                                            key={source}
                                            label={`${source}: ${count.toLocaleString()}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No source data</Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Hours Worked */}
                        {user?.hours_worked !== undefined && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Hours with Activity</Typography>
                                <Typography variant="h5" color="info.main">
                                    {user.hours_worked}h
                                </Typography>
                            </Box>
                        )}

                        {/* Daily Activity Chart */}
                        {activity && activity.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Daily Activity (Last 30 days)</Typography>
                                <DailyChart data={activity} />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Select a user to view their details
                    </Typography>
                )}
            </Box>
        </Drawer>
    )
}
